import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { IconEdit, IconEye, IconChartBar } from "@tabler/icons-react";
import { InlineEditableTable } from "../InlineEditableTable";
import { Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import dayjs from "dayjs";
import DeleteSurveyModal from "./Delete";
import SwitchSurvey from "./Switch";
import SurveyForm from "./SurveyForm";
import { useCreateSurvey, usePatchSurvey } from "../../api/survey";
import { useLanguage } from "../../services/LanguageContext";
import { useGetSurveyById } from "../../api/survey";
import { useGetEvent } from "../../api/event";

function SurveyTable({
  data,
  isLoading,
  eventId,
  page,
  setPage,
  newRowSignal,
}) {
  const tableRef = useRef(null);

  const { mutate: createSurvey } = useCreateSurvey();
  const { language } = useLanguage();
  const [editingSurveyId, setEditingSurveyId] = useState(null);

  const [inlineEditSurveyId, setInlineEditSurveyId] = useState(null);
  const { mutate: updateSurvey } = usePatchSurvey(inlineEditSurveyId);
  const { data: event } = useGetEvent(eventId);

  const records = data?.data || [];
  const totalRecords = data?.pagination?.total || 0;

  const { data: survey } = useGetSurveyById(editingSurveyId, {
    enabled: editingSurveyId !== null,
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    if (newRowSignal > 0) {
      tableRef.current?.addNewRow();
    }
  }, [newRowSignal]);

  return (
    <>
      <InlineEditableTable
        ref={tableRef}
        renderHeader={() => null}
        data={records}
        columns={[
          {
            accessor: `titleI18n.${language}`,
            title: `Title`,
            editable: true,
            inputProps: { placeholder: "Survey title" },
            render: (row) => row.title || row.titleI18n?.[language],
          },
          {
            accessor: `descriptionI18n.${language}`,
            title: `Description`,
            editable: true,
            inputProps: { placeholder: "Survey description" },
            render: (row) => row.description || row.descriptionI18n?.[language],
          },
          {
            accessor: "createdAt",
            title: "Created At",
            editable: false,
            render: (row) => dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss"),
          },
          {
            accessor: "questionCount",
            title: "Questions",
            editable: false,
          },
          {
            accessor: "responseCount",
            title: "Responses",
            editable: false,
          },
          {
            accessor: "isLive",
            title: "Is Live",
            editable: false,
            width: "0%",
            render: (survey) => {
              if (!survey.id) return null;
              return (
                <SwitchSurvey
                  surveyId={survey.id}
                  checked={survey.isLive}
                  numOfQuestions={survey.questionCount}
                  onChange={() => {}}
                />
              );
            },
          },
          {
            accessor: "actions",
            title: "Actions",
            editable: false,
            render: (survey) => {
              if (!survey.id) return null;

              return (
                <Group gap="xs" wrap="nowrap" justify="right">
                  <Tooltip label="Edit survey">
                    <ActionIcon
                      variant="light"
                      onClick={() => setEditingSurveyId(survey.id)}
                    >
                      <IconEdit size={24} />
                    </ActionIcon>
                  </Tooltip>
                  <DeleteSurveyModal surveyId={survey.id} />
                  <Tooltip label="View questions">
                    <ActionIcon
                      variant="light"
                      component={Link}
                      to={`./${survey.id}/questions`}
                    >
                      <IconEye size={24} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="View responses">
                    <ActionIcon
                      variant="light"
                      component={Link}
                      to={`./${survey.id}/responses`}
                    >
                      <IconChartBar size={24} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              );
            },
          },
        ]}
        onRowCreate={(row) => {
          createSurvey({
            eventId,
            titleI18n: row.titleI18n,
            descriptionI18n: row.descriptionI18n,
          });
        }}
        onRowChange={(row) => {
          setInlineEditSurveyId(row.id);

          updateSurvey({
            titleI18n: row.titleI18n,
            descriptionI18n: row.descriptionI18n,
          });
        }}
        validateRow={(row) => {
          const errors = {};
          if (!row.titleI18n?.[language]) {
            errors[`titleI18n.${language}`] = "Title is required";
          }
          return errors;
        }}
        createEmptyRow={() => ({
          titleI18n: {
            [language]: "",
          },
          descriptionI18n: {
            [language]: "",
          },
        })}
        tableProps={{
          fetching: isLoading,
          totalRecords: totalRecords,
          recordsPerPage: data?.pagination?.perPage || 10,
          page: page,
          onPageChange: handlePageChange,
        }}
      />

      <SurveyForm
        opened={editingSurveyId !== null}
        onClose={() => setEditingSurveyId(null)}
        surveyId={editingSurveyId}
        survey={survey}
        event={event}
      />
    </>
  );
}

export default SurveyTable;
